// ═══════════════════════════════════════════════════════════════
// vue-i18n — Configuration bilingue FR/EN
// ═══════════════════════════════════════════════════════════════

import { createI18n } from 'vue-i18n';
import fr from '../locales/fr.json';
import en from '../locales/en.json';

const supportedLocales = (import.meta.env.VITE_SUPPORTED_LOCALES || 'fr,en').split(',');
const defaultLocale = import.meta.env.VITE_DEFAULT_LOCALE || 'fr';

// Détection initiale : localStorage > navigateur > défaut
function detectLocale() {
  const stored = localStorage.getItem('locale');
  if (stored && supportedLocales.includes(stored)) return stored;

  const browser = navigator.language.slice(0, 2);
  if (supportedLocales.includes(browser)) return browser;

  return defaultLocale;
}

export const i18n = createI18n({
  legacy: false, // Composition API mode
  locale: detectLocale(),
  fallbackLocale: defaultLocale,
  messages: { fr, en },
  numberFormats: {
    fr: {
      currency: { style: 'currency', currency: 'EUR', currencyDisplay: 'narrowSymbol' },
      decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 4 },
      percent: { style: 'percent', minimumFractionDigits: 1 },
    },
    en: {
      currency: { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol' },
      decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 4 },
      percent: { style: 'percent', minimumFractionDigits: 1 },
    },
  },
  datetimeFormats: {
    fr: {
      short: { year: 'numeric', month: '2-digit', day: '2-digit' },
      long: { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' },
    },
    en: {
      short: { year: 'numeric', month: '2-digit', day: '2-digit' },
      long: { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' },
    },
  },
});

export function setLocale(locale) {
  if (!supportedLocales.includes(locale)) return;
  i18n.global.locale.value = locale;
  localStorage.setItem('locale', locale);
  document.documentElement.lang = locale;
}

// Locale native par devise — donne le format conventionnel
// (position symbole, séparateurs décimal/milliers)
const CURRENCY_LOCALES = {
  EUR: 'fr-FR',  // 100,00 €
  USD: 'en-US',  // $100.00
  CAD: 'en-CA',  // 100,00 $
  GBP: 'en-GB',  // £100.00
  CHF: 'de-CH',  // CHF 100.00
  MUR: 'en-MU',  // Rs 100.00
  MGA: 'fr-MG',  // Ariary malgache (Ar) — 0 décimale (géré par Intl)
  // Devises francophones du référentiel pays (backend lib/countries.js) ;
  // les autres devises retombent sur en-US (format Intl standard).
  XPF: 'fr-NC',  // 100 F CFP
  XOF: 'fr-SN',  // 100 F CFA
  XAF: 'fr-CM',  // 100 FCFA
  KMF: 'fr-KM',
  MAD: 'fr-MA',
  TND: 'fr-TN',
  DZD: 'fr-DZ',
  MRU: 'fr-MR',
  GNF: 'fr-GN',
  CDF: 'fr-CD',
  RWF: 'fr-RW',
  BIF: 'fr-BI',
  DJF: 'fr-DJ',
  HTG: 'fr-HT',
  SCR: 'en-SC',
};

let currentCurrency = 'EUR';
export function getCurrencyLocale(currency) {
  return CURRENCY_LOCALES[currency] || 'en-US';
}
export function getCurrentCurrency() {
  return currentCurrency;
}

// Symbole « étroit » de la devise (€, Ar, Rs, $…) pour l'afficher dans un libellé.
// Fallback sur le code ISO si le symbole n'est pas résolu.
export function getCurrencySymbol(currency) {
  const cur = currency || currentCurrency;
  try {
    const parts = new Intl.NumberFormat(getCurrencyLocale(cur), {
      style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value || cur;
  } catch {
    return cur;
  }
}

// Met à jour la devise courante pour le helper fmtMoney.
// Appelé à chaque login/impersonation depuis auth.store.
export function setCurrency(currency) {
  if (!currency) return;
  currentCurrency = currency;
  // On garde aussi les numberFormats vue-i18n pour `n(v, 'currency')` (fallback)
  for (const loc of supportedLocales) {
    i18n.global.setNumberFormat(loc, {
      currency: { style: 'currency', currency, currencyDisplay: 'narrowSymbol' },
      decimal:  { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 4 },
      percent:  { style: 'percent', minimumFractionDigits: 1 },
    });
  }
}

// Helper de formatage monétaire utilisant la locale native de la devise.
// Usage dans templates : {{ fmtMoney(data.stockValue) }}
//                       {{ fmtMoney(data.stockValue, 'EUR') }}
export function fmtMoney(value, currency) {
  const cur = currency || currentCurrency;
  return new Intl.NumberFormat(getCurrencyLocale(cur), {
    style: 'currency',
    currency: cur,
    currencyDisplay: 'narrowSymbol',
  }).format(value ?? 0);
}
